import { PhotoTrigger } from '../photo-viewer.tsx';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fileApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { Alert, Button, LoadingBlock } from '../ui.tsx';
import { useSession } from '../../state/session.tsx';

/**
 * Before/after photos on a visit: proof of the state of the place, taken on
 * site. Thumbnails render inline (the download route serves the bytes after
 * the same tenant check as any file), and upload is a plain camera-friendly
 * file input — on a phone this opens the camera directly.
 */
export function VisitPhotos({ appointmentId }: { appointmentId: string }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const photos = useQuery({
    queryKey: ['visit-photos', appointmentId],
    queryFn: () => fileApi.listForAppointment(appointmentId),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['visit-photos', appointmentId] });

  const upload = useMutation({
    mutationFn: (file: File) => fileApi.uploadVisitPhoto(appointmentId, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('visitPhotos.uploadError')),
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => fileApi.remove(fileId),
    onSuccess: invalidate,
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('visitPhotos.deleteError')),
  });

  const items = photos.data?.items ?? [];
  // Null caps mean the plan has no limit — never compare against them.
  const usage = photos.data?.usage;
  const atCap = usage && !usage.unlimited && usage.maxCount !== null ? usage.count >= usage.maxCount : false;

  return (
    <section className="mt-4 border-t border-[color:var(--color-line)] pt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{t('visitPhotos.title')}</h3>
        {can('files:write') || can('visit_photos:write') ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                event.target.value = '';
              }}
            />
            <Button busy={upload.isPending} disabled={atCap} onClick={() => inputRef.current?.click()}>
              {t('visitPhotos.addPhoto')}
            </Button>
          </>
        ) : null}
      </div>

      {error ? (
        <div className="mb-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {atCap ? <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('visitPhotos.atCap')}</p> : null}

      {photos.isPending ? <LoadingBlock label={t('visitPhotos.loading')} /> : null}
      {photos.isError ? (
        <Alert tone="error" title={t('visitPhotos.loadError')}>
          <Button className="mt-2" busy={photos.isFetching} onClick={() => void photos.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}

      {photos.isSuccess && items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('visitPhotos.empty')}</p>
      ) : null}
      {items.length > 0 ? (
        <ul className="flex flex-wrap gap-3">
          {items.map((photo) => (
            <li key={photo.id} className="w-28">
              <PhotoTrigger
                src={photo.downloadUrl}
                alt={photo.originalFilename ?? t('visitPhotos.photoAlt')}
                group={`visit-${appointmentId}`}
                filename={photo.originalFilename ?? undefined}
              >
                <img
                  src={photo.downloadUrl}
                  alt={photo.originalFilename ?? t('visitPhotos.photoAlt')}
                  className="h-24 w-28 rounded-lg border border-[color:var(--color-line)] object-cover"
                  loading="lazy"
                />
              </PhotoTrigger>
              {can('files:write') ? (
                <button
                  type="button"
                  className="mt-1 text-xs text-[color:var(--color-danger)] hover:underline"
                  onClick={() => {
                    if (window.confirm(t('visitPhotos.confirmDelete'))) remove.mutate(photo.id);
                  }}
                >
                  {t('common.remove')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
